/**
 *
 * ANIMATE COUNT UP
 *
 * Observes the element and animates a number count-up from 0 to the target value
 * specified in the element's `data-topic-number` attribute when it appears in the viewport.
 *
 * - Preserves decimal precision when needed.
 * - Avoids floating-point bugs in large numbers.
 *
 * @param {HTMLElement} element - The DOM element whose text content will be animated.
 * @param {number} [duration=2000] - Total animation duration in milliseconds (data-animate-speed attribute fallback)
 * @param {number} [steps=60] - Number of steps for the count-up animation.
 *
 * @returns {void}
 */

export function animateCountUp(element, duration = 1000, steps = 60) {
  if (!element || !(element instanceof HTMLElement)) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const endValue = parseFloat(element.getAttribute('data-animate-count-up'));
          const animateSpeed = parseInt(element.getAttribute('data-animate-speed')) || duration;

          if (isNaN(endValue)) return;

          const isInteger = Number.isInteger(endValue);
          const increment = endValue / steps;
          const delay = animateSpeed / steps;
          let current = 0;

          element.textContent = '0';

          const interval = setInterval(() => {
            current += increment;

            let displayValue;
            if (current >= endValue) {
              displayValue = isInteger
                ? Math.round(endValue).toLocaleString()
                : parseFloat(endValue.toFixed(2)).toLocaleString(undefined, { maximumFractionDigits: 2 });
              element.textContent = displayValue;
              clearInterval(interval);
            } else {
              displayValue = isInteger
                ? Math.round(current).toLocaleString()
                : parseFloat(current.toFixed(2)).toLocaleString(undefined, { maximumFractionDigits: 2 });
              element.textContent = displayValue;
            }
          }, delay);

          obs.unobserve(element); // run only once
        }
      });
    },
    {
      threshold: 0.5,
    },
  );

  observer.observe(element);
}
